import React from 'react';
import { compose } from 'redux';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import { createSlug } from '../../util/urlHelpers';
import { ResponsiveImage, NamedLink } from '../../components';
import { FiEdit } from 'react-icons/fi';

import css from './PortfolioWorkCard.css';

const PortfolioWorkCardComponent = ({
  portfolioWork,
  orderMode,
  selectedOrder,
  onSelectOrder,
  allSlotsFilled,
  isEditable,
  intl,
}) => {
  const images = portfolioWork.images || [];
  const dynamicImageData = portfolioWork.attributes.publicData.dynamicImageData || [];
  const dynamicImageIds = dynamicImageData?.map(d => d.imageId);
  const staticImage = images.filter(img => !dynamicImageIds.includes(img.id?.uuid || img.id));
  const title = portfolioWork.attributes.title || '';
  const published = portfolioWork?.attributes?.state === 'published';
  const privateWork = portfolioWork?.attributes?.publicData?.privateWork || false;

  const workId = portfolioWork?.id?.uuid || portfolioWork?.id;
  const currentOrder = selectedOrder?.[workId];

  // Determine link target based on published state
  const linkName = published ? 'PortfolioWorkPageCanonical' : 'PortfolioWorkPageDraftPreview';
  const params = published
    ? { id: portfolioWork.id.uuid, slug: createSlug(title) }
    : { id: portfolioWork.id.uuid, slug: createSlug(title), type: 'draft', preview: 'preview' };

  const draftLabel = <FormattedMessage id="PortfolioWorkCard.draftLabel" />;
  const privateLabel = <FormattedMessage id="PortfolioWorkCard.privateLabel" />;

  // Can only feature published, non-private works
  const canBeOrdered = published && !privateWork;

  // Show badge if: in order mode AND can be ordered AND (not all slots filled OR this card is selected)
  const showBadge = orderMode && canBeOrdered && (!allSlotsFilled || currentOrder);

  const handleOrderClick = e => {
    e.preventDefault();
    e.stopPropagation();
    if (onSelectOrder && canBeOrdered) {
      onSelectOrder(workId);
    }
  };

  const cardContent = (
    <>
      <div className={css.portfolioWorkCardImageWrapper}>
        {!published && <div className={css.portfolioWorkCardDraftLabel}>{draftLabel}</div>}
        {published && privateWork && (
          <div className={css.portfolioWorkCardPrivateLabel}>{privateLabel}</div>
        )}
        {showBadge && (
          <div
            className={currentOrder ? css.orderBadgeSelected : css.orderBadge}
            onClick={handleOrderClick}
            title={
              currentOrder
                ? intl.formatMessage({ id: 'PortfolioWorkCard.removeOrder' })
                : intl.formatMessage({ id: 'PortfolioWorkCard.addOrder' })
            }
          >
            {currentOrder || ''}
          </div>
        )}
        {isEditable && (
          <NamedLink
            className={css.editButton}
            name="EditPortfolioWorkPage"
            params={{ id: portfolioWork.id.uuid, slug: createSlug(title), type: 'edit' }}
            onClick={e => e.stopPropagation()}
          >
            <FiEdit />
          </NamedLink>
        )}
        <ResponsiveImage
          className={css.portfolioWorkCardImage}
          alt={title}
          image={staticImage[0] || null}
          variants={['horizontal']}
        />
        <div className={css.portfolioWorkCardTitle}>{title}</div>
      </div>
    </>
  );

  // If in order mode, render without link
  if (orderMode) {
    return (
      <div
        className={published && !privateWork ? css.portfolioWorkCard : css.portfolioWorkCardDraft}
      >
        {cardContent}
      </div>
    );
  }

  return (
    <NamedLink
      className={published && !privateWork ? css.portfolioWorkCard : css.portfolioWorkCardDraft}
      name={linkName}
      params={params}
    >
      {cardContent}
    </NamedLink>
  );
};

const PortfolioWorkCard = compose(injectIntl)(PortfolioWorkCardComponent);
export default PortfolioWorkCard;
